import React from 'react';
import {withRouter} from 'react-router';
import PropTypes from 'prop-types';
import {loadStripe} from '@stripe/stripe-js';
import {isNil} from 'ramda';
import styled from 'styled-components';
import {Form, List, Header, Container, Message, Icon} from 'semantic-ui-react';
import {
  createSubscriptionPayment,
  createSubscriptionPaymentSession,
  retrieveSchool, retrieveSubscriptions
} from '../../api';
import {getSchoolProperty, madeSubscriptionPayment} from '../../util/Session';
import {BlueContainer} from '../../components/Container/ContainerMix';
import {ButtonsGroup, GreenButton} from '../../components/Button/ButtonMix';
import MenuButton from '../../components/Button/MenuButton';
import {centsToDollars} from '../../util/Utils';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const Logo = styled.img`
  max-width: 205px;
`;

const Heading = styled.div`
  text-align: center;
  padding: 30px 0 10px;
  & > h1 {
    color: #ffffff;
  }
  & > p {
    color: #ffffff;
  }
`;

const FullHeightBlueContainer = styled(BlueContainer)`
  height: calc(100vh);
  overflow-y: auto;
  & a {
    color: #ffffff !important;
  }
`;

const SliderWrapper = styled(Form.Field)`
  position: relative;
  margin: 40px 0 !important;
  & .rangeslider__labels {
    margin-top: 5px;
    & .rangeslider__label-item {
      color: #ffffff;
    }
  }
  & .rangeslider-horizontal {
    & .rangeslider__fill {
      background-color: #15C278;
    }
  }
`;

const StyledList = styled(List)`
  color: #ffffff;
  & a {
    color: #0A8DE2
    text-decoration: underline;
  }
`;

const PrecessedIcon = styled(Icon.Group)`
  margin: calc(30vh - 50px) auto 20px auto;
  color: #ffffff;
`;

class SubscriptionPayments extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      school: null,
      success: false,
      message: null,
      selectedSubscription: null,
      subscriptions: [],
      numStudents: 101
    };
  }

  componentDidMount() {
    const {router} = this.props;
    const query = new URLSearchParams(window.location.search);
    const {numStudents} = this.state;

    retrieveSubscriptions()
      .then(response => {
        const {subscriptions} = response.data;
        const selectedSubscription = subscriptions.find(sub => sub.min_students <= numStudents && sub.max_students >= numStudents);
        this.setState({subscriptions, selectedSubscription});
      });

    const schoolId = getSchoolProperty('id');
    return retrieveSchool(schoolId)
      .then(response => {
        this.setState({school: response.data});

        // This means the user just paid successfully! Record the payment in our system and redirect them
        // to the dashboard
        if (query.get('success')) {
          this.setState({success: true, message: 'Payment successful!'});

          const stripeCheckoutSessionId = query.get('session_id');

          // Make a payment, save in our Session this user made a payment, then let them go to the Dashboard!
          return createSubscriptionPayment(this.state.school.id, stripeCheckoutSessionId)
            .then(() => madeSubscriptionPayment())
            .then(() => router.push('/dashboard'));
        }

        // This means the user cancelled the payment from the Stripe screen. Just display a message.
        if (query.get('canceled')) {
          this.setState({message: 'Payment canceled.'});
        }
      });
  }

  handleChange = (e, {name, value}) => {
    const {subscriptions} = this.state;
    const subscription = subscriptions.find(sub => sub.min_students <= value && sub.max_students >= value);
    this.setState({[name]: value, selectedSubscription: subscription});
  };

  handleDismiss = () => this.setState({ message: null });

  render() {
    const {onClickSidebar} = this.props;
    const {school, message, success, numStudents, selectedSubscription} = this.state;

    const handleClick = () => stripePromise
      .then(stripe => createSubscriptionPaymentSession(school.id, numStudents)
        .then(response => {
          const stripeCheckoutSessionId = response.data.id;
          return stripe.redirectToCheckout({sessionId: stripeCheckoutSessionId});
        }));

    // Payment was successful! Show loading while we redirect to the /dashboard
    if (success) {
      return (
        <FullHeightBlueContainer>
          <PrecessedIcon size="huge">
            <Icon loading size="big" name="circle notch" />
            <Icon name="check" />
          </PrecessedIcon>

          <Header as="h3" icon textAlign="center" style={{color: '#ffffff', marginTop: 'calc(100hv - 100px)'}}>
            <Header.Content>Payment successful!</Header.Content>
          </Header>
        </FullHeightBlueContainer>
      );
    }

    return !isNil(school) ?
      (
        <FullHeightBlueContainer>
          <MenuButton onClick={onClickSidebar}/>
          <Form onSubmit={handleClick}>
            <Heading>
              <Logo src="/images/logotype-white.svg" alt="Logo"/>
            </Heading>
            <Container textAlign="center" style={{color: '#ffffff'}}>
              <p>Enter the number of students who will be using BodyBank at any one given time.</p>
              {selectedSubscription && <p><b>One Time Cost: {centsToDollars(selectedSubscription.amount)} + processing fee</b></p>}
            </Container>
            <SliderWrapper>
              <Form.Input value={numStudents} label="Number of students" type="number" name="numStudents" placeholder="100" onChange={this.handleChange} required/>
            </SliderWrapper>
            <Header as="h4" icon textAlign="left" style={{color: '#ffffff', marginTop: '60px'}}>
              <Header.Content>Cost Includes:</Header.Content>
            </Header>
            <StyledList bulleted style={{color: '#ffffff'}}>
              <List.Item>BodyBank web-based app for all students & staff</List.Item>
              <List.Item>Coordinator account to designate staff from students, track user points, & schedule interval settings</List.Item>
              <List.Item>Printable PDF for the BodyBank <a target="_blank" href="https://www.bodybank.co/poster-collection">Poster Collection</a> (provided upon request)</List.Item>
            </StyledList>
            {!isNil(message) ? (
              <Message 
                onDismiss={this.handleDismiss}
                header={message}
                negative
              />
) : 
              null}
            <ButtonsGroup>
              <GreenButton type="submit" id="checkout-button" fluid>Proceed to Checkout</GreenButton>
            </ButtonsGroup>
          </Form>
        </FullHeightBlueContainer>
      ) :
      null;
  }
}

SubscriptionPayments.propTypes = {
  router: PropTypes.object.isRequired,
  onClickSidebar: PropTypes.func.isRequired
};

export default withRouter(SubscriptionPayments);



// WEBPACK FOOTER //
// ./src/pages/Subscriptions/SubscriptionPayments.js